import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { useCallback, useState } from 'react'
import { ENTITY_TYPES } from '@sales-center/common'
import { SideDrawer, Tab, TabPanel } from '@sales-center/components'
import { UpdateContactFormWrapper } from './UpdateContactFormWrapper'
import { MatchContact } from './MatchContact'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  attachContact as leadAttachContact,
  ILeadDetail,
} from '@user/store/lead'
import {
  attachContact as opportunityAttachContact,
  IOpportunityDetail,
} from '@user/store/opportunity'
import AppBar from '@mui/material/AppBar'
import Tabs from '@mui/material/Tabs'

interface Props {
  contactId?: number
  entityId: number
  entityType: ENTITY_TYPES
  initialTab?: number
  isPrimaryContact?: boolean
  onClose: () => void
  open: boolean
  detail: ILeadDetail | IOpportunityDetail
}

const getStyles = (theme: Theme) => ({
  appBar: {
    backgroundColor: '#f9f9f9',
  },

  tab: {
    padding: `0 ${theme.spacing(4)}`,
    whiteSpace: 'nowrap',
  },

  tabPanel: {
    padding: theme.spacing(2.4),
  },
})

export function UpdateContactDrawer(props: Props) {
  const {
    contactId,
    entityId,
    entityType,
    initialTab = 1,
    isPrimaryContact,
    onClose,
    open,
    detail,
  } = props

  const dispatch = useAppDispatch()

  const title = contactId ? 'Edit Contact' : 'Add Contact'

  //========== S T A T E ==========
  const [activeTab, setActiveTab] = useState(initialTab)
  const [highlightText, setHighlightText] = useState<string>('')

  //========== E V E N T S ==========
  const onChangeTab = useCallback(
    (_e: React.ChangeEvent<unknown>, value: number) => setActiveTab(value),
    [],
  )

  const handleSearchTextChange = useCallback((text: string) => {
    setHighlightText(text)
  }, [])

  const handleClose = useCallback(() => {
    setActiveTab(1)
    setHighlightText('')
    if (onClose) {
      onClose()
    }
  }, [onClose])

  const handleAddContact = useCallback(
    (contactId: number) => {
      if (entityType === ENTITY_TYPES.Lead && entityId) {
        dispatch(
          leadAttachContact({
            contactId: contactId,
            leadId: entityId,
          }),
        )
      }
      if (entityType === ENTITY_TYPES.Opportunity && entityId) {
        dispatch(
          opportunityAttachContact({
            contactId: contactId,
            opportunityId: entityId,
          }),
        )
      }
      handleClose()
    },

    [dispatch, entityId, entityType, handleClose],
  )
  const styles = getStyles(useTheme())
  return (
    <SideDrawer
      title={title}
      open={open}
      onClose={handleClose}
      width={550}
      disableBackdropClick={true}
    >
      {contactId ? (
        <UpdateContactFormWrapper
          contactId={contactId}
          entityId={entityId}
          entityType={entityType}
          isPrimaryContact={isPrimaryContact}
          onClose={handleClose}
        />
      ) : (
        <>
          <AppBar
            sx={styles.appBar}
            color="default"
            elevation={1}
            position="sticky"
          >
            <Tabs value={activeTab} onChange={onChangeTab}>
              <Tab
                value={1}
                label="Add Existing Contact"
                sx={styles.tab}
                data-test-id="addExistingContactTab"
              />
              <Tab
                value={2}
                label="Add New Contact"
                sx={styles.tab}
                data-test-id="addNewContactTab"
              />
            </Tabs>
          </AppBar>
          <TabPanel tab={activeTab} value={1} style={styles.tabPanel}>
            <MatchContact
              onContactSet={handleAddContact}
              onSearchTextChange={handleSearchTextChange}
              onSwitchToCreate={() => setActiveTab(2)}
              searchText={highlightText}
              detail={detail}
            />
          </TabPanel>
          <TabPanel tab={activeTab} value={2}>
            <UpdateContactFormWrapper
              contactId={contactId}
              entityId={entityId}
              entityType={entityType}
              isPrimaryContact={isPrimaryContact}
              onClose={handleClose}
            />
          </TabPanel>
        </>
      )}
    </SideDrawer>
  )
}
